import { useForwardRef } from '@zorro/web/ui-utils';
import _noop from 'lodash/noop';
import { ForwardedRef, MutableRefObject, forwardRef } from 'react';
import { usePlacesWidget } from 'react-google-autocomplete';

import { Select } from '../v2/Select';
import { TextInput, type TextInputProps } from '../v2/TextInput';

interface IAddressInputProps extends TextInputProps {
  notifySubscribers?: (addressValue: string) => void;
}

export const ZorroAddressInput = forwardRef(
  (
    { notifySubscribers = _noop, size = 'md', ...props }: IAddressInputProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const addressInputRef = useForwardRef<HTMLInputElement | null>(ref);

    const handleNotify = (addressValue: string) => {
      notifySubscribers(addressValue);
    };

    const { ref: addressRef } = usePlacesWidget({
      apiKey: process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY,
      options: {
        componentRestrictions: { country: 'us' },
        types: ['address'],
      },
      onPlaceSelected: (place) => {
        if (addressInputRef.current && place?.formatted_address) {
          addressInputRef.current.value = place.formatted_address;
          handleNotify(addressInputRef.current.value);
        }
      },
    });

    return (
      <TextInput
        ref={(component: HTMLInputElement) => {
          /**
           * INFO: We want to avoid firing requests against the Google Places API
           * when running automated e2e tests to keep our usage and costs low.
           * Since our e2e tests just type out the full addresses into a text field
           * and don't rely on the autocompletion, this should be fine.
           * See more: https://app.clickup.com/t/860pr92qn
           */
          if (
            process.env.NEXT_PUBLIC_IS_E2E_RUN !== 'true' &&
            component instanceof HTMLInputElement
          ) {
            addressInputRef.current = component;
            (addressRef as MutableRefObject<HTMLInputElement | null>).current =
              component;
          }
        }}
        size={size}
        {...props}
      />
    );
  }
);

ZorroAddressInput.displayName = 'ZorroAddressInput';

type SelectCountyProps = {
  counties: string[];
};

export const SelectCounty = ({ counties }: SelectCountyProps) => {
  return <Select data={counties} placeholder="Select county of residence" />;
};
